import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowDownToLine, ArrowLeftRight, Gamepad2 } from "lucide-react";

export default function Transfer() {
  const { refresh, user } = useAuth();
  const [tab, setTab] = useState("main_to_deposit");
  const [wallets, setWallets] = useState(null);
  const [playActive, setPlayActive] = useState(null);

  // Main → own Deposit
  const [m2dAmount, setM2dAmount] = useState(9);
  const [m2dTpass, setM2dTpass] = useState("");
  const [m2dMsg, setM2dMsg] = useState({ type: "", text: "" });
  const [m2dBusy, setM2dBusy] = useState(false);

  // P2P deposit → deposit
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState(9);
  const [tpass, setTpass] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [recipient, setRecipient] = useState(null); // {found, full_name} | null
  const [p2pHistory, setP2pHistory] = useState([]);

  // Deposit → Game Fund (LP)
  const [d2gAmount, setD2gAmount] = useState(5);
  const [d2gTpass, setD2gTpass] = useState("");
  const [d2gMsg, setD2gMsg] = useState({ type: "", text: "" });
  const [d2gBusy, setD2gBusy] = useState(false);

  const loadWallets = useCallback(async () => {
    try {
      const r = await api.get("/me/wallets");
      setWallets(r.data);
    } catch (err) {
      console.warn("Wallets load failed:", err?.message);
    }
  }, []);
  useEffect(() => { loadWallets(); }, [loadWallets]);

  const loadP2PHistory = useCallback(async () => {
    try {
      const r = await api.get("/me/transfers");
      setP2pHistory(r.data.rows || []);
    } catch (err) {
      console.warn("P2P history load failed:", err?.message);
    }
  }, []);
  useEffect(() => { loadP2PHistory(); }, [loadP2PHistory]);

  useEffect(() => {
    api.get("/me/play-card")
      .then((r) => setPlayActive(!!r.data?.play_eligible))
      .catch(() => setPlayActive(false));
  }, []);

  const subActive = Number(user?.package_value || 0) > 0;
  const p2pEligible = subActive && playActive === true;
  const p2pEligibilityMsg = !subActive
    ? "You need an active subscription to transfer funds. Please activate a package first."
    : playActive === false
      ? "You must be actively playing the Play Card game to transfer funds — fund your Game Fund and drop at least one card first."
      : null;

  // Live recipient-name lookup (debounced) when entering the Recipient User ID.
  useEffect(() => {
    const id = (to || "").trim();
    if (!id) { setRecipient(null); return; }
    const handle = setTimeout(async () => {
      try {
        const r = await api.get(`/users/lookup?user_no=${encodeURIComponent(id)}`);
        setRecipient(r.data);
      } catch {
        setRecipient(null);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [to]);

  const submitMainToDeposit = async (e) => {
    e.preventDefault(); setM2dBusy(true); setM2dMsg({ type: "", text: "" });
    try {
      const r = await api.post("/wallet/main-to-deposit", { amount: Number(m2dAmount), t_pass: m2dTpass });
      setM2dMsg({ type: "ok", text: `Moved $${r.data.sent.toFixed(2)} → Deposit credited $${r.data.credited_to_deposit.toFixed(2)} (deduction $${r.data.deduction.toFixed(2)})` });
      setM2dTpass("");
      await refresh();
      await loadWallets();
    } catch (e) {
      setM2dMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setM2dBusy(false); }
  };

  const submitP2P = async (e) => {
    e.preventDefault(); setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/wallet/transfer", { to_user_id: to, amount: Number(amount), t_pass: tpass });
      setMsg({ type: "ok", text: `Sent $${r.data.sent.toFixed(2)} → recipient deposit $${r.data.received.toFixed(2)}` });
      setTo(""); setTpass("");
      await refresh();
      await loadWallets();
      await loadP2PHistory();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const submitDepositToGame = async (e) => {
    e.preventDefault(); setD2gBusy(true); setD2gMsg({ type: "", text: "" });
    try {
      const r = await api.post("/wallet/deposit-to-game", { amount: Number(d2gAmount), t_pass: d2gTpass });
      setD2gMsg({ type: "ok", text: `Moved $${r.data.moved.toFixed(2)} → Game Fund credited $${r.data.credited_to_game_fund.toFixed(2)} (no deduction)` });
      setD2gTpass("");
      await refresh();
      await loadWallets();
    } catch (e) {
      setD2gMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setD2gBusy(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Fund Transfer</div>
        <h1 className="font-display text-4xl mt-1">Move Funds</h1>
        <p className="text-neutral-400 mt-2 max-w-2xl">
          Main wallet holds your withdrawable income. Move funds to Deposit to spend on packages or transfer to your upline/downline. Deposit funds <strong className="text-neutral-200">cannot be withdrawn</strong> or moved back to Main.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 max-w-3xl">
        <div className="lt-card p-5">
          <div className="overline">Main wallet</div>
          <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="transfer-main-bal">${(wallets?.main || 0).toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mt-1">Withdrawable</div>
        </div>
        <div className="lt-card p-5">
          <div className="overline">Deposit balance</div>
          <div className="font-display text-3xl tabular mt-2" data-testid="transfer-deposit-bal">${(wallets?.deposit || 0).toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mt-1">For packages & P2P transfer</div>
        </div>
        <div className="lt-card p-5">
          <div className="overline">NFT Card Fund</div>
          <div className="font-display text-3xl tabular mt-2" data-testid="transfer-lp-bal">${(wallets?.lp || 0).toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mt-1">For Play Card game</div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setTab("main_to_deposit")}
          data-testid="transfer-tab-main-to-deposit"
          className={`px-4 py-2 text-sm rounded flex items-center gap-2 ${tab === "main_to_deposit" ? "bg-[#D4AF37] text-black" : "lt-btn-secondary"}`}
        >
          <ArrowDownToLine size={14} /> Main → Deposit
        </button>
        <button
          onClick={() => setTab("p2p_deposit")}
          data-testid="transfer-tab-p2p"
          className={`px-4 py-2 text-sm rounded flex items-center gap-2 ${tab === "p2p_deposit" ? "bg-[#D4AF37] text-black" : "lt-btn-secondary"}`}
        >
          <ArrowLeftRight size={14} /> Deposit → Upline/Downline
        </button>
        <button
          onClick={() => setTab("deposit_to_game")}
          data-testid="transfer-tab-deposit-to-game"
          className={`px-4 py-2 text-sm rounded flex items-center gap-2 ${tab === "deposit_to_game" ? "bg-[#D4AF37] text-black" : "lt-btn-secondary"}`}
        >
          <Gamepad2 size={14} /> Deposit → Game Fund
        </button>
      </div>

      {tab === "main_to_deposit" && (
        <form onSubmit={submitMainToDeposit} className="lt-card p-6 max-w-xl space-y-4" data-testid="m2d-form">
          <div>
            <div className="overline">Convert Main → Deposit</div>
            <p className="text-xs text-neutral-500 mt-1">
              20% deduction applies (10% Admin + 10% Donation Club). Net amount credited to your Deposit.
            </p>
          </div>
          <div>
            <label className="overline block mb-2">Amount from Main (USD)</label>
            <input
              type="number" step="1" min="9"
              className="lt-input tabular"
              value={m2dAmount}
              onChange={(e) => setM2dAmount(e.target.value)}
              required
              data-testid="m2d-amount-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="overline">Gross</div><div className="tabular mt-1">${Number(m2dAmount || 0).toFixed(2)}</div></div>
            <div><div className="overline">Deduction 20%</div><div className="tabular mt-1 text-red-400/80">${(Number(m2dAmount || 0) * 0.2).toFixed(2)}</div></div>
            <div><div className="overline">You receive</div><div className="tabular mt-1 gold-text">${(Number(m2dAmount || 0) * 0.8).toFixed(2)}</div></div>
          </div>
          <div>
            <label className="overline block mb-2">Transaction password</label>
            <input
              type="password" className="lt-input"
              value={m2dTpass}
              onChange={(e) => setM2dTpass(e.target.value)}
              required
              data-testid="m2d-tpass-input"
            />
          </div>
          {m2dMsg.text && <div className={`text-sm ${m2dMsg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="m2d-msg">{m2dMsg.text}</div>}
          <button className="lt-btn-primary" disabled={m2dBusy} data-testid="m2d-submit-btn">
            {m2dBusy ? "Processing…" : "Convert Main → Deposit"}
          </button>
        </form>
      )}

      {tab === "p2p_deposit" && (
        <div className="space-y-6">
        <form onSubmit={submitP2P} className="lt-card p-6 max-w-xl space-y-4" data-testid="p2p-form">
          <div>
            <div className="overline">P2P Transfer — Deposit → Deposit</div>
            <p className="text-xs text-neutral-500 mt-1">
              Direct upline or downline only. Amount must be greater than $0.50. Once per day. No deduction (you already paid 20% on Main → Deposit).
            </p>
          </div>
          <div>
            <label className="overline block mb-2">Recipient User ID</label>
            <input
              className="lt-input tabular"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="e.g. 100023"
              required
              data-testid="p2p-to-input"
            />
            {to.trim() && recipient && (
              recipient.found ? (
                <div className="text-xs mt-1.5 gold-text" data-testid="p2p-recipient-name">
                  ✓ {recipient.full_name || "(no name)"}
                </div>
              ) : (
                <div className="text-xs mt-1.5 text-red-400" data-testid="p2p-recipient-name">
                  No user found with this ID
                </div>
              )
            )}
          </div>
          <div>
            <label className="overline block mb-2">Amount (USD, more than $0.50)</label>
            <input
              type="number" step="1" min="9"
              className="lt-input tabular"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              data-testid="p2p-amount-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="overline">You send</div><div className="tabular mt-1">${Number(amount || 0).toFixed(2)}</div></div>
            <div><div className="overline">Recipient gets</div><div className="tabular mt-1 gold-text">${Number(amount || 0).toFixed(2)}</div></div>
          </div>
          <div>
            <label className="overline block mb-2">Transaction password</label>
            <input
              type="password" className="lt-input"
              value={tpass}
              onChange={(e) => setTpass(e.target.value)}
              required
              data-testid="p2p-tpass-input"
            />
          </div>
          {msg.text && <div className={`text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="p2p-msg">{msg.text}</div>}
          {p2pEligibilityMsg && <div className="text-sm text-amber-400" data-testid="p2p-eligibility-msg">{p2pEligibilityMsg}</div>}
          <button className="lt-btn-primary disabled:opacity-50" disabled={busy || !p2pEligible} data-testid="p2p-submit-btn">
            {busy ? "Sending…" : "Send to upline/downline"}
          </button>
          </form>

          <div className="lt-card p-6 max-w-3xl" data-testid="p2p-history">
            <div className="overline mb-3">Transfer History — Deposit → Upline/Downline</div>
            {p2pHistory.length === 0 ? (
              <div className="text-sm text-neutral-500">No transfers yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="lt-table min-w-[560px]">
                  <thead>
                    <tr><th>Date</th><th>Type</th><th>User</th><th>Amount</th></tr>
                  </thead>
                  <tbody>
                    {p2pHistory.map((h) => (
                      <tr key={h.id} data-testid={`p2p-history-row-${h.id}`}>
                        <td className="text-neutral-400">{new Date(h.created_at).toLocaleString()}</td>
                        <td>
                          <span className={`text-xs uppercase tracking-widest ${h.direction === "sent" ? "text-amber-400" : "text-emerald-400"}`}>
                            {h.direction === "sent" ? "Sent" : "Received"}
                          </span>
                        </td>
                        <td>
                          <div className="tabular">{h.counterparty_no}</div>
                          <div className="text-xs text-neutral-500">{h.counterparty_name || "—"}</div>
                        </td>
                        <td className="tabular gold-text">
                          {h.direction === "sent" ? "-" : "+"}${Number(h.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "deposit_to_game" && (
        <form onSubmit={submitDepositToGame} className="lt-card p-6 max-w-xl space-y-4" data-testid="d2g-form">
          <div>
            <div className="overline">Deposit → NFT Card Fund</div>
            <p className="text-xs text-neutral-500 mt-1">
              Move funds from Deposit to Game Fund to play the Play Card game. <strong className="text-neutral-200">No deduction</strong> — 1:1 conversion.
            </p>
          </div>
          <div>
            <label className="overline block mb-2">Amount from Deposit (USD)</label>
            <input
              type="number" step="0.01" min="0.01"
              className="lt-input tabular"
              value={d2gAmount}
              onChange={(e) => setD2gAmount(e.target.value)}
              required
              data-testid="d2g-amount-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="overline">You move</div><div className="tabular mt-1">${Number(d2gAmount || 0).toFixed(2)}</div></div>
            <div><div className="overline">Game Fund gets</div><div className="tabular mt-1 gold-text">${Number(d2gAmount || 0).toFixed(2)}</div></div>
          </div>
          <div>
            <label className="overline block mb-2">Transaction password</label>
            <input
              type="password" className="lt-input"
              value={d2gTpass}
              onChange={(e) => setD2gTpass(e.target.value)}
              required
              data-testid="d2g-tpass-input"
            />
          </div>
          {d2gMsg.text && <div className={`text-sm ${d2gMsg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="d2g-msg">{d2gMsg.text}</div>}
          <button className="lt-btn-primary" disabled={d2gBusy} data-testid="d2g-submit-btn">
            {d2gBusy ? "Moving…" : "Move to Game Fund"}
          </button>
        </form>
      )}
    </div>
  );
}
